import { TemplatePortal } from '@angular/cdk/portal';
import type { OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import {
  HostBinding,
  ChangeDetectionStrategy,
  Component,
  Inject,
  InjectionToken,
  Input,
  Optional,
  TemplateRef,
  ViewChild,
  ViewContainerRef,
  ViewEncapsulation,
  ContentChild,
} from '@angular/core';
import type { CanDisable } from '../../core/common';
import { mixinDisabled } from '../../core/common';
import { MatTabLabelDirective } from '@shared/material/tabs/tab-label.directive';
import { Subject } from 'rxjs';
import { MAT_TAB, MAT_TAB_LABEL } from '../tab-label.directive';

// Boilerplate for applying mixins to MatTab.
const _MatTabMixinBase = mixinDisabled(class {});

/**
 * Used to provide a tab group to a tab without causing a circular dependency.
 */
export const MAT_TAB_GROUP = new InjectionToken<any>('MAT_TAB_GROUP');

@Component({
  selector: 'app-mat-tab',
  templateUrl: 'tab.component.html',
  // eslint-disable-next-line @angular-eslint/prefer-on-push-component-change-detection
  changeDetection: ChangeDetectionStrategy.Default,
  encapsulation: ViewEncapsulation.None,
  exportAs: 'matTab',
  providers: [{ provide: MAT_TAB, useExisting: MatTabComponent }],
})
export class MatTabComponent extends _MatTabMixinBase implements CanDisable, OnInit, OnChanges, OnDestroy {
  @Input() override disabled: boolean;

  // This binding is used to ensure that the component ID doesn't clash with the `MatTab`.
  @HostBinding('attr.mat-id-collision')
  readonly _matIdCollision = null;

  /** Template inside the MatTab view that contains an `<ng-content>`. */
  @ViewChild(TemplateRef, { static: true }) _implicitContent: TemplateRef<any>;

  /** Plain text label for the tab, used when there is no template label. */
  // eslint-disable-next-line @angular-eslint/no-input-rename
  @Input('label') textLabel: string = '';

  /** Aria label for the tab. */
  @Input('aria-label') ariaLabel: string;

  /**
   * Reference to the element that the tab is labelled by.
   * Will be cleared if `aria-label` is set at the same time.
   */
  @Input('aria-labelledby') ariaLabelledby: string;

  /**
   * Classes to be passed to the tab label inside the mat-tab-header container.
   * Supports string and string array values, same as `ngClass`.
   */
  @Input() labelClass: string | string[];

  /**
   * Classes to be passed to the tab mat-tab-body container.
   * Supports string and string array values, same as `ngClass`.
   */
  @Input() bodyClass: string | string[];

  get content(): TemplatePortal | null {
    return this._contentPortal;
  }

  /** Emits whenever the internal state of the tab changes. */
  readonly _stateChanges = new Subject<void>();

  /**
   * The relatively indexed position where 0 represents the center, negative is left, and positive
   * represents the right.
   */
  position: number | null = null;

  /**
   * The initial relatively index origin of the tab if it was created and selected after there
   * was already a selected tab. Provides context of what position the tab should originate from.
   */
  origin: number | null = null;

  /**
   * Whether the tab is currently active.
   */
  isActive = false;

  /** Content for the tab label given by `<ng-template app-mat-tab-label>`. */
  protected _templateLabel: MatTabLabelDirective;

  /** Portal that will be the hosted content of the tab */
  private _contentPortal: TemplatePortal | null = null;

  constructor(
    private _viewContainerRef: ViewContainerRef,
    @Inject(MAT_TAB_GROUP) @Optional() public _closestTabGroup: any,
  ) {
    super();
  }

  /** Content for the tab label given by `<ng-template app-mat-tab-label>`. */
  @ContentChild(MAT_TAB_LABEL)
  get templateLabel(): MatTabLabelDirective {
    return this._templateLabel;
  }
  set templateLabel(value: MatTabLabelDirective) {
    // Only update the label if the query managed to find one. This works around an issue where a
    // user may have manually set `templateLabel` during creation mode, which would then get
    // clobbered by `undefined` when the query resolves. Also note that we check that the closest
    // tab matches the current one so that we don't pick up labels from nested tabs.
    if (value?._closestTab === this) {
      this._templateLabel = value;
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      Object.prototype.hasOwnProperty.call(changes, 'textLabel') ||
      Object.prototype.hasOwnProperty.call(changes, 'disabled')
    ) {
      this._stateChanges.next();
    }
  }

  ngOnDestroy(): void {
    this._stateChanges.complete();
  }

  ngOnInit(): void {
    this._contentPortal = new TemplatePortal(this._implicitContent, this._viewContainerRef);
  }
}
